import { useState } from 'react'
import { useAccessRequests, useAccessRequestActions } from '../../hooks/useAccessRequests'
import { AccessRequestItem } from '../../api/accessRequests'

const STATUS_CONFIG = {
  pending:  { label: 'En attente', bg: '#FEF3C7', color: '#92400E' },
  approved: { label: 'Approuvé',   bg: '#D1FAE5', color: '#065F46' },
  rejected: { label: 'Refusé',     bg: '#FEE2E2', color: '#991B1B' },
} as const

const ROLE_LABELS: Record<string, string> = {
  expediteur: 'Expéditeur',
  livreur:    'Livreur',
  admin:      'Admin',
}

const ROLE_COLORS: Record<string, { bg: string; color: string }> = {
  expediteur: { bg: '#EFF6FF', color: '#1D4ED8' },
  livreur:    { bg: '#F0FDF4', color: '#15803D' },
  admin:      { bg: '#FDF4FF', color: '#7E22CE' },
}

export default function AdminAccessRequests() {
  const [status, setStatus] = useState('all')
  const [role,   setRole]   = useState('all')
  const [search, setSearch] = useState('')
  const [page,   setPage]   = useState(1)

  // Approve flow
  const [approveModal, setApproveModal]   = useState<AccessRequestItem | null>(null)
  const [approveResult, setApproveResult] = useState<{
    name: string; email: string; tempPassword: string
  } | null>(null)
  const [copied, setCopied] = useState(false)

  // Reject flow
  const [rejectModal, setRejectModal]   = useState<{ id: string; name: string } | null>(null)
  const [rejectReason, setRejectReason] = useState('')

  const { data, isLoading } = useAccessRequests({ status, role, search, page })
  const { approve, reject } = useAccessRequestActions()

  const handleApproveConfirm = async () => {
    if (!approveModal) return
    try {
      const result = await approve.mutateAsync(approveModal._id)
      setApproveResult({
        name:         approveModal.name,
        email:        approveModal.email,
        tempPassword: result.tempPassword,
      })
      setApproveModal(null)
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error
      alert(msg ?? "Erreur lors de l'approbation")
    }
  }

  const handleReject = async () => {
    if (!rejectModal) return
    try {
      await reject.mutateAsync({ id: rejectModal.id, reason: rejectReason })
      setRejectModal(null)
      setRejectReason('')
    } catch {
      alert('Erreur lors du refus')
    }
  }

  const copyPassword = () => {
    if (!approveResult?.tempPassword) return
    navigator.clipboard.writeText(approveResult.tempPassword)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  return (
    <div style={{ padding: '24px 32px', maxWidth: 1200, margin: '0 auto' }}>

      {/* En-tête */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 700, color: '#0F172A', margin: 0 }}>
            Demandes d'accès
          </h1>
          <p style={{ color: '#64748B', fontSize: 14, margin: '4px 0 0' }}>
            Gérez les inscriptions soumises depuis la landing page
          </p>
        </div>
        {data && (
          <div style={{ display: 'flex', gap: 8 }}>
            {(['pending', 'approved', 'rejected'] as const).map(s => (
              <div key={s} style={{
                padding: '6px 14px', borderRadius: 20, fontSize: 13, fontWeight: 600,
                background: STATUS_CONFIG[s].bg, color: STATUS_CONFIG[s].color,
              }}>
                {STATUS_CONFIG[s].label}: {data.requests.filter(r => r.status === s).length}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Filtres */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
        <input
          type="text"
          placeholder="Rechercher (nom, email, tél)…"
          value={search}
          onChange={e => { setSearch(e.target.value); setPage(1) }}
          style={{
            flex: 1, minWidth: 220, padding: '9px 14px', borderRadius: 8,
            border: '1px solid #E2E8F0', outline: 'none', fontSize: 14,
          }}
        />
        <select
          value={status}
          onChange={e => { setStatus(e.target.value); setPage(1) }}
          style={{ padding: '9px 14px', borderRadius: 8, border: '1px solid #E2E8F0', fontSize: 14 }}
        >
          <option value="all">Tous les statuts</option>
          <option value="pending">En attente</option>
          <option value="approved">Approuvés</option>
          <option value="rejected">Refusés</option>
        </select>
        <select
          value={role}
          onChange={e => { setRole(e.target.value); setPage(1) }}
          style={{ padding: '9px 14px', borderRadius: 8, border: '1px solid #E2E8F0', fontSize: 14 }}
        >
          <option value="all">Tous les rôles</option>
          <option value="expediteur">Expéditeur</option>
          <option value="livreur">Livreur</option>
          <option value="admin">Admin</option>
        </select>
      </div>

      {/* Tableau */}
      <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #E2E8F0', overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ background: '#F8FAFC' }}>
              {['Demandeur', 'Rôle', 'Volume', 'Statut', 'Date', 'Actions'].map(h => (
                <th key={h} style={{
                  padding: '12px 16px', textAlign: 'left', fontSize: 12, fontWeight: 600,
                  color: '#64748B', textTransform: 'uppercase', letterSpacing: '.05em',
                  borderBottom: '1px solid #E2E8F0',
                }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {isLoading && (
              <tr><td colSpan={6} style={{ padding: 40, textAlign: 'center', color: '#94A3B8' }}>
                Chargement…
              </td></tr>
            )}
            {!isLoading && data?.requests.length === 0 && (
              <tr><td colSpan={6} style={{ padding: 40, textAlign: 'center', color: '#94A3B8' }}>
                Aucune demande trouvée
              </td></tr>
            )}
            {data?.requests.map((item, i) => (
              <tr key={item._id} style={{
                borderBottom: i < data.requests.length - 1 ? '1px solid #F1F5F9' : 'none',
              }}>
                <td style={{ padding: '14px 16px' }}>
                  <div style={{ fontWeight: 600, color: '#0F172A', fontSize: 14 }}>{item.name}</div>
                  <div style={{ color: '#64748B', fontSize: 13 }}>{item.email}</div>
                  <div style={{ color: '#64748B', fontSize: 13 }}>{item.phone}</div>
                  {item.company && (
                    <div style={{ color: '#94A3B8', fontSize: 12, marginTop: 2 }}>{item.company}</div>
                  )}
                </td>

                <td style={{ padding: '14px 16px' }}>
                  <span style={{
                    padding: '4px 10px', borderRadius: 6, fontSize: 12, fontWeight: 600,
                    background: ROLE_COLORS[item.role]?.bg,
                    color:      ROLE_COLORS[item.role]?.color,
                  }}>
                    {ROLE_LABELS[item.role]}
                  </span>
                </td>

                <td style={{ padding: '14px 16px', color: '#475569', fontSize: 13 }}>
                  {item.volume ?? '—'}
                </td>

                <td style={{ padding: '14px 16px' }}>
                  <span style={{
                    padding: '4px 10px', borderRadius: 6, fontSize: 12, fontWeight: 600,
                    background: STATUS_CONFIG[item.status].bg,
                    color:      STATUS_CONFIG[item.status].color,
                  }}>
                    {STATUS_CONFIG[item.status].label}
                  </span>
                  {item.rejectedReason && (
                    <div style={{ color: '#94A3B8', fontSize: 11, marginTop: 4 }}>{item.rejectedReason}</div>
                  )}
                </td>

                <td style={{ padding: '14px 16px', color: '#64748B', fontSize: 13 }}>
                  {new Date(item.createdAt).toLocaleDateString('fr-FR', {
                    day: '2-digit', month: 'short', year: 'numeric',
                  })}
                </td>

                <td style={{ padding: '14px 16px' }}>
                  {item.status === 'pending' && (
                    <div style={{ display: 'flex', gap: 8 }}>
                      <button
                        onClick={() => setApproveModal(item)}
                        style={{
                          padding: '6px 14px', borderRadius: 6, border: 0, cursor: 'pointer',
                          background: '#10B981', color: '#fff', fontSize: 13, fontWeight: 600,
                        }}
                      >
                        ✓ Approuver
                      </button>
                      <button
                        onClick={() => setRejectModal({ id: item._id, name: item.name })}
                        style={{
                          padding: '6px 14px', borderRadius: 6, border: '1px solid #FCA5A5',
                          cursor: 'pointer', background: '#FFF', color: '#DC2626', fontSize: 13, fontWeight: 600,
                        }}
                      >
                        ✕ Refuser
                      </button>
                    </div>
                  )}
                  {item.status === 'approved' && item.createdUserId && (
                    <span style={{ color: '#10B981', fontSize: 13 }}>Compte créé ✓</span>
                  )}
                  {item.status === 'rejected' && (
                    <span style={{ color: '#94A3B8', fontSize: 13 }}>Traité</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      </div>

      {/* Pagination */}
      {data && data.pagination.totalPages > 1 && (
        <div style={{ display: 'flex', gap: 8, justifyContent: 'center', marginTop: 20 }}>
          {Array.from({ length: data.pagination.totalPages }, (_, i) => i + 1).map(p => (
            <button
              key={p}
              onClick={() => setPage(p)}
              style={{
                width: 36, height: 36, borderRadius: 8, border: '1px solid #E2E8F0',
                background: p === page ? '#0F2A4A' : '#fff',
                color:      p === page ? '#fff' : '#374151',
                cursor: 'pointer', fontWeight: 600, fontSize: 14,
              }}
            >{p}</button>
          ))}
        </div>
      )}

      {/* ── Modal approbation ── */}
      {approveModal && (
        <ModalOverlay onClose={() => setApproveModal(null)}>
          <div style={{ marginBottom: 20 }}>
            <div style={{
              width: 48, height: 48, borderRadius: 14, background: '#D1FAE5',
              display: 'grid', placeItems: 'center', fontSize: 22, marginBottom: 14,
            }}>✓</div>
            <h3 style={{ margin: '0 0 6px', color: '#0F172A', fontSize: 18, fontWeight: 700 }}>
              Approuver la demande
            </h3>
            <p style={{ color: '#64748B', fontSize: 14, margin: 0 }}>
              Un compte sera créé et un mot de passe temporaire généré.
            </p>
          </div>

          {/* Récap demandeur */}
          <div style={{
            background: '#F8FAFC', borderRadius: 12, padding: '16px 18px',
            display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 20,
          }}>
            <InfoRow icon="👤" label="Nom"      value={approveModal.name} />
            <InfoRow icon="✉"  label="Email"    value={approveModal.email} />
            <InfoRow icon="📞" label="Téléphone" value={approveModal.phone} />
            {approveModal.company && (
              <InfoRow icon="🏢" label="Entreprise" value={approveModal.company} />
            )}
            <InfoRow
              icon="🎭"
              label="Rôle"
              value={
                <span style={{
                  padding: '3px 10px', borderRadius: 6, fontSize: 12, fontWeight: 600,
                  background: ROLE_COLORS[approveModal.role]?.bg,
                  color:      ROLE_COLORS[approveModal.role]?.color,
                }}>
                  {ROLE_LABELS[approveModal.role]}
                </span>
              }
            />
          </div>

          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
            <button onClick={() => setApproveModal(null)} style={btnGhost}>
              Annuler
            </button>
            <button
              onClick={handleApproveConfirm}
              disabled={approve.isPending}
              style={{ ...btnPrimary, background: '#10B981', opacity: approve.isPending ? 0.6 : 1 }}
            >
              {approve.isPending ? 'Création…' : '✓ Créer le compte'}
            </button>
          </div>
        </ModalOverlay>
      )}

      {/* ── Modal résultat (mot de passe) ── */}
      {approveResult && (
        <ModalOverlay onClose={() => { setApproveResult(null); setCopied(false) }}>
          <div style={{ textAlign: 'center', marginBottom: 20 }}>
            <div style={{
              width: 56, height: 56, borderRadius: 99, background: '#D1FAE5',
              display: 'grid', placeItems: 'center', fontSize: 26,
              margin: '0 auto 14px',
            }}>✓</div>
            <h3 style={{ margin: '0 0 6px', color: '#0F172A', fontSize: 18, fontWeight: 700 }}>
              Compte créé avec succès
            </h3>
            <p style={{ color: '#64748B', fontSize: 14, margin: 0 }}>
              Le compte de <strong>{approveResult.name}</strong> a été créé.
            </p>
          </div>

          <div style={{
            background: '#F8FAFC', border: '1px solid #E2E8F0',
            borderRadius: 12, padding: '16px 18px', marginBottom: 20,
          }}>
            <p style={{ fontSize: 12, fontWeight: 600, color: '#64748B', textTransform: 'uppercase', letterSpacing: '.06em', margin: '0 0 10px' }}>
              Mot de passe temporaire
            </p>
            <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
              <code style={{
                flex: 1, padding: '11px 14px', borderRadius: 8,
                background: '#0F172A', color: '#7CB342',
                fontFamily: 'JetBrains Mono, monospace', fontSize: 16, fontWeight: 700,
                letterSpacing: '.1em', border: 'none',
              }}>
                {approveResult.tempPassword}
              </code>
              <button
                onClick={copyPassword}
                style={{
                  padding: '11px 16px', borderRadius: 8, border: '1px solid #E2E8F0',
                  background: copied ? '#D1FAE5' : '#fff', cursor: 'pointer',
                  fontSize: 13, fontWeight: 600,
                  color: copied ? '#065F46' : '#374151',
                  transition: 'all .2s', whiteSpace: 'nowrap',
                }}
              >
                {copied ? '✓ Copié' : 'Copier'}
              </button>
            </div>
            <p style={{ margin: '12px 0 0', fontSize: 12, color: '#F59E0B', display: 'flex', alignItems: 'center', gap: 6 }}>
              <span>⚠</span>
              Communiquez ce mot de passe à <strong>{approveResult.email}</strong> par email ou SMS.
            </p>
          </div>

          <button
            onClick={() => { setApproveResult(null); setCopied(false) }}
            style={{ ...btnPrimary, width: '100%', justifyContent: 'center' }}
          >
            Fermer
          </button>
        </ModalOverlay>
      )}

      {/* ── Modal refus ── */}
      {rejectModal && (
        <ModalOverlay onClose={() => setRejectModal(null)}>
          <div style={{ marginBottom: 20 }}>
            <div style={{
              width: 48, height: 48, borderRadius: 14, background: '#FEE2E2',
              display: 'grid', placeItems: 'center', fontSize: 22, marginBottom: 14,
            }}>✕</div>
            <h3 style={{ margin: '0 0 6px', color: '#0F172A', fontSize: 18, fontWeight: 700 }}>
              Refuser la demande de {rejectModal.name}
            </h3>
            <p style={{ color: '#64748B', fontSize: 14, margin: 0 }}>
              Optionnel : indiquez un motif (non envoyé au demandeur pour l'instant)
            </p>
          </div>
          <textarea
            placeholder="Motif du refus…"
            value={rejectReason}
            onChange={e => setRejectReason(e.target.value)}
            rows={3}
            style={{
              width: '100%', padding: '10px 12px', borderRadius: 8,
              border: '1px solid #E2E8F0', fontSize: 14, resize: 'vertical',
              fontFamily: 'inherit', boxSizing: 'border-box', marginBottom: 16,
            }}
          />
          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
            <button onClick={() => setRejectModal(null)} style={btnGhost}>
              Annuler
            </button>
            <button
              onClick={handleReject}
              disabled={reject.isPending}
              style={{ ...btnPrimary, background: '#DC2626', opacity: reject.isPending ? 0.6 : 1 }}
            >
              {reject.isPending ? 'Refus…' : 'Confirmer le refus'}
            </button>
          </div>
        </ModalOverlay>
      )}
    </div>
  )
}

// ── Composants utilitaires ────────────────────────────────

function ModalOverlay({ children, onClose }: { children: React.ReactNode; onClose: () => void }) {
  return (
    <div
      style={{
        position: 'fixed', inset: 0, background: 'rgba(15,23,42,.45)',
        backdropFilter: 'blur(4px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 9999, padding: 20,
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: '#fff', borderRadius: 20, padding: 28, width: '100%', maxWidth: 440,
          boxShadow: '0 24px 64px rgba(0,0,0,.18)',
          animation: 'fadeUp .2s ease',
        }}
        onClick={e => e.stopPropagation()}
      >
        <style>{`@keyframes fadeUp { from { opacity:0; transform:translateY(12px); } to { opacity:1; transform:none; } }`}</style>
        {children}
      </div>
    </div>
  )
}

function InfoRow({ icon, label, value }: { icon: string; label: string; value: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 14 }}>
      <span style={{ fontSize: 15, width: 20, textAlign: 'center', flexShrink: 0 }}>{icon}</span>
      <span style={{ color: '#94A3B8', fontSize: 12, fontWeight: 600, width: 72, flexShrink: 0 }}>{label}</span>
      <span style={{ color: '#0F172A', fontWeight: 500 }}>{value}</span>
    </div>
  )
}

const btnGhost: React.CSSProperties = {
  padding: '9px 18px', borderRadius: 8, border: '1px solid #E2E8F0',
  background: '#fff', cursor: 'pointer', fontSize: 14, fontWeight: 500,
  color: '#374151',
}

const btnPrimary: React.CSSProperties = {
  padding: '9px 18px', borderRadius: 8, border: 0,
  background: '#0F2A4A', color: '#fff', cursor: 'pointer',
  fontSize: 14, fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 6,
}
